import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import { websocketClient } from '@/services/websocket'
import type { User } from '@/types'

// Set by ZBox Commander's preview bootstrap. When true, Google OAuth is
// bypassed and the app runs as a mock admin user — Google rejects the
// sandbox's dynamic *.preview.ziro.io URLs as unauthorized JS origins,
// so a real auth round-trip can never succeed inside the preview.
const IS_ZBOX_PREVIEW = import.meta.env.VITE_ZBOX_PREVIEW === 'true'

const ZBOX_MOCK_USER: User = {
  id: 'zbox-demo-user-001',
  email: 'demo@ziro.io',
  full_name: 'ZBox Demo Admin',
  role: 'admin' as User['role'],
  avatar_url: undefined,
  is_active: 'true',
  last_login: new Date().toISOString(),
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
}
const ZBOX_MOCK_TOKEN = 'zbox-preview-mock-token'

export class AuthStore {
  user: User | null = null
  isAuthenticated = false
  isLoading = false
  error: string | null = null

  constructor() {
    makeAutoObservable(this)
    this.initializeAuth()
  }

  private async initializeAuth() {
    // In ZBox preview mode, skip any backend call and render logged-in
    // immediately. The app's network layer is separately mocked by the
    // preview-proxy, so any api call the app makes will resolve to a
    // benign 200 — but we don't need to wait for that to happen before
    // showing the UI.
    if (IS_ZBOX_PREVIEW) {
      localStorage.setItem('access_token', ZBOX_MOCK_TOKEN)
      runInAction(() => {
        this.user = ZBOX_MOCK_USER
        this.isAuthenticated = true
      })
      return
    }
    const token = localStorage.getItem('access_token')
    if (token) {
      try {
        await this.fetchCurrentUser()
      } catch (error) {
        this.logout()
      }
    }
  }

  async loginWithGoogle() {
    // ZBox preview: skip Google entirely — our dynamic preview URL isn't
    // a registered OAuth origin and Google would reject the redirect.
    // Seed a mock admin so the rest of the app renders its authenticated
    // state unchanged.
    if (IS_ZBOX_PREVIEW) {
      localStorage.setItem('access_token', ZBOX_MOCK_TOKEN)
      runInAction(() => {
        this.user = ZBOX_MOCK_USER
        this.isAuthenticated = true
        this.isLoading = false
      })
      return
    }

    this.isLoading = true
    this.error = null

    try {
      console.log('AuthStore: Fetching Google auth URL...')
      const response = await apiClient.getGoogleAuthUrl()
      console.log('AuthStore: Got response:', response)

      // Store state for CSRF validation (backend validates this)
      localStorage.setItem('oauth_state', response.state)

      // Redirect to Google OAuth.
      //
      // Use the TOP window (window.top) rather than the iframe.
      // Google blocks OAuth flows initiated from inside an iframe
      // (returns 403 on /accountchooser as a click-jacking defense).
      // Navigating the top frame works in both contexts:
      //   - Standalone (agents.ziro.pub): window.top === window, no-op
      //     difference vs. window.location.
      //   - ZBox preview iframe (<sid>-zbox.ziro.pub inside zbox.ziro.pub):
      //     navigates the parent commander page to Google. After
      //     Google auth, the bridge's /__zbox/auth/google/callback
      //     handles the code exchange and 302's back to
      //     <sid>-zbox.ziro.pub/auth/callback?tokens. The user briefly
      //     leaves the commander UI; the session subdomain
      //     localStorage receives the tokens and the next iframe load
      //     in commander picks them up automatically.
      console.log('AuthStore: Redirecting to:', response.authorization_url)
      try {
        if (window.top && window.top !== window) {
          window.top.location.href = response.authorization_url
        } else {
          window.location.href = response.authorization_url
        }
      } catch {
        // Some browsers throw on cross-origin window.top access. Fall
        // back to in-frame navigation; if Google rejects, the user
        // sees the same 403 we had before this fix.
        window.location.href = response.authorization_url
      }
    } catch (error: any) {
      console.error('AuthStore: Error fetching Google auth URL:', error)
      runInAction(() => {
        const detail = error.response?.data?.detail
        if (typeof detail === 'string') {
          this.error = detail
        } else if (Array.isArray(detail)) {
          this.error = detail.map((err: any) => err.msg).join(', ')
        } else if (typeof detail === 'object' && detail !== null) {
          this.error = JSON.stringify(detail)
        } else {
          this.error = 'Failed to initiate Google login'
        }
        this.isLoading = false
      })
      throw error
    }
  }

  async handleOAuthCallback(accessToken: string, refreshToken: string) {
    this.isLoading = true
    this.error = null

    try {
      // Store tokens
      localStorage.setItem('access_token', accessToken)
      localStorage.setItem('refresh_token', refreshToken)

      // Clear OAuth state
      localStorage.removeItem('oauth_state')

      // Fetch user info
      const user = await apiClient.getCurrentUser()

      runInAction(() => {
        this.user = user
        this.isAuthenticated = true
      })

      // Initialize WebSocket connection
      this.initializeWebSocket(accessToken, user.id)

      return user
    } catch (error: any) {
      // Clear tokens on error
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')

      runInAction(() => {
        const detail = error.response?.data?.detail
        if (typeof detail === 'string') {
          this.error = detail
        } else if (error.message) {
          this.error = error.message
        } else if (Array.isArray(detail)) {
          this.error = detail.map((err: any) => err.msg).join(', ')
        } else if (typeof detail === 'object' && detail !== null) {
          this.error = JSON.stringify(detail)
        } else {
          this.error = 'OAuth callback failed'
        }
      })
      throw error
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async fetchCurrentUser() {
    this.isLoading = true

    try {
      const user = await apiClient.getCurrentUser()

      runInAction(() => {
        this.user = user
        this.isAuthenticated = true
      })

      // Initialize WebSocket connection
      const token = localStorage.getItem('access_token')
      if (token && user.id) {
        this.initializeWebSocket(token, user.id)
      }

      return user
    } catch (error) {
      runInAction(() => {
        this.user = null
        this.isAuthenticated = false
      })
      throw error
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  logout() {
    apiClient.logout().catch(() => { })

    // Disconnect WebSocket
    websocketClient.disconnect()

    // Clear localStorage tokens
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('oauth_state')

    runInAction(() => {
      this.user = null
      this.isAuthenticated = false
      this.error = null
    })
  }

  private initializeWebSocket(token: string, userId: string) {
    try {
      websocketClient.connect(token, userId)
      console.log('WebSocket connection initialized')
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error)
    }
  }

  clearError() {
    this.error = null
  }

  get isAdmin() {
    return this.user?.role === 'admin'
  }

  get isDeveloper() {
    return this.user?.role === 'developer' || this.isAdmin
  }

  get isViewer() {
    return this.user?.role === 'viewer' || this.isDeveloper
  }
}

export const authStore = new AuthStore()
